import { BCMSRecord, BCMSStateManagerCRUD, useBCMSContext, useRoute, useStateManager, useWorkflow } from "@bcm-dev/bcms-vite";
import { CenterContentContainer, ContentHeaderView, FormContainer, FormRow, FormRowView, PageContainer, PageContentView } from "components/shared";
import { FormButtonRow } from "components/shared/containers/FormContainer";
import { PrimaryButton, SecondaryButton } from "components/ui";
import Classes, { ACHTemplate, Company, CompanyAccount } from "model";
import React from "react";

import "./styles.css";
import "./mobile.css";



/**
 * @function ImportFileReviewPage
 * @return {React.Component}
 * @exported true
 * @description The main component for the ImportFileReviewPage.
 */
export default function ImportFileReviewPage(props) {
    const { goStep, prevStep, nextStep } = useWorkflow() ?? {};

    const context = useBCMSContext();
    BCMSRecord.globalControllerFor(Classes, context);


    const data = { ...getDefaultData(), ...props.data ?? {} };
    const ui = { ...getDefaultUI(), ...props.ui ?? {} };

    const controller = (state, setState) => ({
        data: state.data,
        ui: state.ui,
        onBack:()=>{
            prevStep();
            window.scrollTo(0, 0);
        },
        onCancel:()=>{
            goStep(0);
            window.scrollTo(0, 0);
        },
        onImport:()=>{
            const template = data.template.record
            const updateTemplate = ACHTemplate.init({ ...template, name: `Import_${template.name}` });
        
            BCMSStateManagerCRUD.init(state)
            .update(["data", "template"], updateTemplate)
            .submit(setState);

            nextStep({ data ,ui});
            window.scrollTo(0, 0);
        },

    })
    return (
        <PageContainer className="import-file-review-page" state={{ data, ui }} controller={controller}>
            <ImportFileReviewPageContent />
        </PageContainer>
    )
}

export const getDefaultData = () => ({
    achToParticipants: {
                participants: []
            },
            participants: [],
            template: ACHTemplate.init({
                id: ACHTemplate.nextID(),
                transactionType: "payment",
                secCode: "PPD Credit - Consumer Credit",
                name: "",
                companyDiscretionaryData: "",
                companyEntryDescription: "",
                creditAmount: "0.00",
                debitAmount: "0.00",
                achToParticipants: [],
                achFromAccount: [],
                company: Company.initByID(0),
                companyAccount: CompanyAccount.initByID(0),
                numberOfAccounts: "0"
            }),
            defaultTemplate:
                ACHTemplate.init({
                    id: ACHTemplate.nextID(),
                    transactionType: "payment",
                    secCode: "PPD Credit - Consumer Credit",
                    name: "SamplePPD",
                    companyDiscretionaryData: "",
                    companyEntryDescription: "",
                    creditAmount: "0.00",
                    debitAmount: "0.00",
                    achToParticipants: [],
                    achFromAccount: [],
                    company: Company.initByID(0),
                    companyAccount: CompanyAccount.initByID(0),
                    numberOfAccounts: "0"
                }),
            numberOfHeaderRows: "",
            numberOfFooterRows: "",
            inputDecimalsIntoAmounts: "No",
            textQualifier: "None",
            fileFormat: "Comma",
            chosenFileName: "SamplePPD.csv",
});

export const getDefaultUI = () => ({

});

/**
 * @function ImportFileReviewPageContent
 * @return {React.Component}
 * @exported true
 * @description The page content component for the ImportFileReviewPage.
 */
function ImportFileReviewPageContent(props) {
    const { ui, data, onBack, onCancel, onImport} = useStateManager();
    const navigate = useRoute();
    const fileName = data.chosenFileName;
    const template = data.template;
    const companyAccount = template.companyAccount;
    const inputDecimalsIntoAmounts = data.inputDecimalsIntoAmounts;
    const numberOfHeaderRows = data.numberOfHeaderRows !== "" ? data.numberOfHeaderRows : "0";
    const numberOfFooterRows = data.numberOfFooterRows !== "" ? data.numberOfFooterRows : "0";
    const textQualifier = data.textQualifier;

    const fileFormat = data.fileFormat;

    const name = props.name ?? "Data contained in file";
    const accountNumber = props.accountNumber ?? "Data contained in file";
    const routingTransit = props.routingTransit ?? "Data contained in file";
    const amount = props.amount ?? "Data contained in file";
    const transactionCode = props.transactionCode ?? "Data contained in file";

    return (
        <PageContentView>
            <CenterContentContainer>
                <ContentHeaderView text={`Import File - ${fileName}`} />
                <FormContainer className="small-form-width">
                    <FormRowView className="company-row" text="Company">
                        <b>{companyAccount.name} - {companyAccount.number}</b>
                    </FormRowView>
                    <FormRowView className="template-name-row" text="Template Name">
                        <b>
                            <span data-point={ `Template_Name_Value` }>{template.name}</span>
                        </b>
                    </FormRowView>
                    <FormRowView className="transaction-type-row" text="Transaction Type">
                        <b>{template.secCode}</b>
                    </FormRowView>
                    <FormRowView className="input-decimals-into-amount" text="Input Decimals into Amount"  >
                        <b>{inputDecimalsIntoAmounts}</b>
                    </FormRowView>
                    <FormRow className="number-of-rows-to-exclude">
                        <h2>Number of Rows to Exclude</h2>
                    </FormRow>
                    <FormRowView className="header-footer-row">
                        <div className="flex-container flex-horizontal header-footer-row-container">
                            <p>Header: <b>{numberOfHeaderRows}</b></p>
                            <p>Footer: <b>{numberOfFooterRows}</b></p>
                        </div>
                    </FormRowView>
                    <FormRowView text="Text Qualifier" className="text-qualifier-row">
                        <b>{textQualifier}</b>
                    </FormRowView>
                    <FormRowView text="File Format" className="file-format-row">
                        <b>{fileFormat}</b>
                    </FormRowView>
                    <FormRow className="field-details-row">
                        <h2>Field Details</h2>
                    </FormRow>
                    <FormRowView text="Name" className="name-row">
                        <b>{name}</b>
                    </FormRowView>
                    <FormRowView text="Account Number" className="account-number-row">
                        <b>{accountNumber}</b>
                    </FormRowView>
                    <FormRowView text="Routing Transit" className="routing-transit-row">
                        <b>{routingTransit}</b>
                    </FormRowView>
                    <FormRowView text="Amount" className="amount-row">
                        <b>{amount}</b>
                    </FormRowView>
                    <FormRowView text="Transaction Code" className="transaction-code-row">
                        <b>{transactionCode}</b>
                    </FormRowView>
                    <FormButtonRow>
                        <SecondaryButton dataPoint={ `Back_Button` } text="Back" onClick={onBack} />
                        <PrimaryButton dataPoint={ `Import_Button` } text="Import" onClick={onImport}/>
                        <SecondaryButton dataPoint={ `Cancel_Button` } text="Cancel" onClick={onCancel} />
                    </FormButtonRow>
                </FormContainer>
            </CenterContentContainer>
        </PageContentView>
    )
}